import i18n from "../../../i18n";import { Trans } from "react-i18next";import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { cn } from "../../../lib/utils";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle } from
"../../../components/ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Textarea } from "../../../components/ui/textarea";
import { LeaveBalanceValue } from "./LeaveBalanceValue";

export function ApproveModal({
  open,
  onOpenChange,
  request,
  onConfirm,
  loading = false,
  error = ""
}) {
  const [remarks, setRemarks] = useState("");

  useEffect(() => {
    if (!open) setRemarks("");
  }, [open]);

  if (!request) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-[#0a1230]/50 backdrop-blur-sm" />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
            "w-[95vw] sm:w-[90vw] md:w-[500px] max-w-[500px]",
            "max-h-[90vh] overflow-hidden flex flex-col",
            "bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60",
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "font-sans"
          )}>
          
          <div className="flex items-center justify-between px-6 py-3.5 border-b border-[var(--color-text-border)]/70 shrink-0">
            <DialogTitle className="text-[20px] font-semibold text-foreground tracking-tight">
              <Trans i18nKey="formActions.approveLeaveRequest" />
            </DialogTitle>
            <DialogPrimitive.Close
              disabled={loading}
              className="h-9 w-9 grid place-items-center rounded-xl text-[var(--color-text-secondary)] hover:bg-[var(--color-workspace)]/60 hover:text-[var(--color-primary)] transition disabled:opacity-50">
              
              <X className="h-5 w-5" />
              <span className="sr-only"><Trans i18nKey="formActions.close" /></span>
            </DialogPrimitive.Close>
          </div>

          <div className="px-6 py-5 space-y-5 overflow-y-auto flex-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-[var(--color-workspace)]/40 p-5 rounded-2xl border border-[var(--color-text-border)]/40 text-sm">
              <div>
                <span className="block text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider mb-1">
                  <Trans i18nKey="common.employeeName" />
                </span>
                <span className="font-semibold text-foreground">{request.employee || "—"}</span>
              </div>
             {request.leaveId != null ?
              <div>
                  <span className="block text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider mb-1">
                    <Trans i18nKey="leave.leaveId" />
                  </span>
                  <span className="font-semibold text-[var(--color-primary)]">
                    #{request.leaveId}
                  </span>
                </div> :
              null}
              <div>
                <span className="block text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider mb-1">
                  <Trans i18nKey="leave.leaveType" />
                </span>
                <span className="font-semibold text-foreground">{request.type}</span>
              </div>
              <div>
                <span className="block text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider mb-1">
                  <Trans i18nKey="dateTime.fromDate" />
                </span>
                <span className="font-medium text-foreground">
                  {request.from} ({request.fromSession || "—"})
                </span>
              </div>
              <div>
                <span className="block text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider mb-1">
                  <Trans i18nKey="dateTime.toDate" />
                </span>
                <span className="font-medium text-foreground">
                  {request.to} ({request.toSession || "—"})
                </span>
              </div>
            
              <div>
                <span className="block text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider mb-1">
                  <Trans i18nKey="dateTime.totalDays" />
                </span>
                <div>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-md bg-[var(--color-workspace)]/80 text-[var(--color-primary)] text-xs font-bold">
                    {request.days} {request.days === 1 ? "Day" : "Days"}
                  </span>
                </div>
              </div>
              <div>
                <span className="block text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider mb-1">
                  <Trans i18nKey="leave.leaveBalance" />
                </span>
                <span className="text-[var(--color-primary)] text-xs font-bold">
                  <LeaveBalanceValue
                    empId={request.empId}
                    leaveTypeId={request.leaveTypeId}
                    enabled={open} />
                
                </span>
              </div>
              <div className="sm:col-span-2">
                <span className="block text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider mb-1">
                  <Trans i18nKey="common.reason" />
                </span>
                <p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap">
                  {request.reason?.trim() ? request.reason : "No reason provided."}
                </p>
              </div>
              
            </div>

            <div className="space-y-2">
              <label htmlFor="approval-remarks" className="text-sm font-semibold text-foreground">
                <Trans i18nKey="leave.comments" />
              </label>
              <Textarea
                id="approval-remarks" rows={2}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder={i18n.t("formActions.addOptionalCommentsForTheEmployee")}
                disabled={loading}
                className="min-h-[0] rounded-xl border-[var(--color-text-border)] resize-none text-sm focus-visible:ring-[var(--color-accent)] focus-visible:border-[var(--color-accent)]" />
              
            </div>

            {error ?
            <p className="text-sm text-[var(--color-error)] font-medium">{error}</p> :
            null}
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 px-6 py-4 border-t border-[var(--color-text-border)]/70 shrink-0">
            <button
              type="button"
              onClick={() => onConfirm(remarks.trim())}
              disabled={loading}
              className={cn(
                "h-11 px-6 rounded-xl bg-[var(--color-primary)] text-white text-sm font-semibold transition hover:bg-[var(--color-accent)] shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] cursor-pointer",
                loading && "opacity-50 cursor-not-allowed"
              )}>
              
              {loading ? "Approving..." : "Approve"}
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>);

}
